import { useMemo, useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ReferenceLine
} from 'recharts';
import { getChinaDate, formatToChinaTime } from '../utils/time';

/**
 * 小时数据接口
 */
interface HourlyData {
  hour: string;          // 小时 (格式: "00", "01", "02", ..., "23")
  sales_increment: number; // 该小时的销量增量（实际或预估）
  actual_increment: number | null; // 实际增量（仅实际数据有值）
  predicted_increment: number | null; // 预估增量（仅预估数据有值）
  transition_increment: number | null; // 连接线增量（从实际到预估的过渡）
  time_label: string;    // 时间显示标签 (格式: "00:00")
  has_data: boolean;     // 该小时是否有实际数据
  is_predicted: boolean; // 是否为预估数据
  is_transition: boolean; // 是否为连接过渡点
}

interface HourlyTrendChartProps {
  data: any[];          // 所有监控记录
  height?: number;
  title?: string;
  productData?: any;    // 产品数据，用于获取货币和地区信息
}



/**
 * 生成完整的24小时数据，包括实际数据、空数据时段和预估数据
 * @param actualData 实际的小时数据
 * @param selectedDate 选中的日期
 * @returns 包含24小时完整数据的数组，前面添加占位空间
 */
const generate24HoursData = (actualData: any[], selectedDate: string, allData: any[] = []): HourlyData[] => {
  const fullDayData: HourlyData[] = [];
  
  // 添加占位空间，避免00:00时段与垂直标尺线重叠
  fullDayData.push({
    hour: 'spacer',
    sales_increment: 0,
    actual_increment: null,
    predicted_increment: null,
    transition_increment: null,
    time_label: '',
    has_data: false,
    is_predicted: false,
    is_transition: false
  });
  
  // 计算现有数据的平均增量，用于预估
  const validIncrements = actualData.filter(item => item.sales_increment > 0).map(item => item.sales_increment);
  const avgIncrement = validIncrements.length > 0 
    ? Math.round(validIncrements.reduce((sum, val) => sum + val, 0) / validIncrements.length)
    : 0;
  
  // 获取当前小时（只在查看今日数据时使用）
  const today = getChinaDate();
  const isToday = selectedDate === today;
  const currentHour = isToday ? parseInt(formatToChinaTime(new Date().toISOString()).split(' ')[1].split(':')[0]) : 23;
  
  // 找到最后一个有实际数据的小时
  let lastActualHour = -1;
  let lastActualValue = 0;
  let firstPredictedHour = -1;
  
  for (let hour = 0; hour < 24; hour++) {
    const hourStr = hour.toString().padStart(2, '0');
    const existingData = actualData.find(item => item.hour === hourStr);
    
    if (existingData) {
      lastActualHour = hour;
      lastActualValue = existingData.sales_increment;
    } else if (hour > currentHour && isToday && firstPredictedHour === -1) {
      firstPredictedHour = hour;
    }
  }

  for (let hour = 0; hour < 24; hour++) {
    const hourStr = hour.toString().padStart(2, '0');
    const existingData = actualData.find(item => item.hour === hourStr);
    
    // 修改时间标签格式为"X时"
    const timeLabel = hour === 0 ? '0时' : `${hour}时`;
    
    if (existingData) {
      // 有实际数据
      fullDayData.push({
        hour: hourStr,
        sales_increment: existingData.sales_increment,
        actual_increment: existingData.sales_increment,
        predicted_increment: null,
        transition_increment: null,
        time_label: timeLabel,
        has_data: true,
        is_predicted: false,
        is_transition: false
      });
    } else if (hour <= currentHour || !isToday) {
      // 当前时间之前但没有数据的时段，设为0（历史日期全部设为0）
      fullDayData.push({
        hour: hourStr,
        sales_increment: 0,
        actual_increment: 0,
        predicted_increment: null,
        transition_increment: null,
        time_label: timeLabel,
        has_data: false,
        is_predicted: false,
        is_transition: false
      });
    } else {
      // 未来时间段预估算法说明：
      // 1. 基础值：使用当日已有时段的平均增量
      // 2. 参考值：使用前一日同时段的增量数据（如果有的话）
      // 3. 加权计算：当日平均增量70% + 前一日同时段30%
      // 4. 合理性检查：预估值不超过当日最大增量的2倍
      // 这种方法避免了时区差异导致的时段特征偏差，更贴近实际销售规律
      
      // 未来时间段，使用改进的预估算法
      let predictedIncrement = 0;
      
      // 1. 当日平均增量作为基础
      const currentDayAvg = avgIncrement;
      
      // 2. 尝试获取前一日同时段的数据作为参考
      const previousDate = new Date(selectedDate);
      previousDate.setDate(previousDate.getDate() - 1);
      const previousDateStr = formatToChinaTime(previousDate.toISOString()).split(' ')[0];
      
             // 查找前一日的原始数据并计算小时增量
       const previousDayRecords = allData.filter(record => {
         if (!record?.created_at) return false;
         const recordDate = record.created_at.split(' ')[0];
         return recordDate === previousDateStr;
       });
       
       // 如果有前一日数据，计算前一日的小时增量数据
       let previousDayHourlyData: any[] = [];
       if (previousDayRecords.length > 0) {
         // 按时间排序
         const sortedPrevRecords = previousDayRecords.sort((a, b) => 
           new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
         );
         
         // 计算每条记录的增量
         const prevRecordsWithIncrements = sortedPrevRecords.map((record, index) => {
           let increment = 0;
           if (index > 0) {
             const previous = sortedPrevRecords[index - 1];
             increment = Math.max(0, record.sales_count - previous.sales_count);
           }
           return {
             ...record,
             sales_increment: increment,
             hour: record.created_at.split(' ')[1].split(':')[0]
           };
         });
         
         // 按小时分组汇总
         for (let h = 0; h < 24; h++) {
           const hStr = h.toString().padStart(2, '0');
           const hourRecords = prevRecordsWithIncrements.filter(r => r.hour === hStr);
           
           if (hourRecords.length > 0) {
             const totalIncrement = hourRecords.reduce((sum, record) => 
               sum + record.sales_increment, 0
             );
             previousDayHourlyData.push({
               hour: hStr,
               sales_increment: totalIncrement
             });
           }
         }
       }
      
             // 如果有前一日同时段数据，进行加权平均
       const previousHourData = previousDayHourlyData.find(item => item.hour === hourStr);
       if (previousHourData && previousHourData.sales_increment > 0) {
         // 当日平均增量权重70%，前一日同时段权重30%
         predictedIncrement = Math.round(currentDayAvg * 0.7 + previousHourData.sales_increment * 0.3);
         // console.log(`时段${hourStr}: 当日平均${currentDayAvg}, 前日同时段${previousHourData.sales_increment}, 预估${predictedIncrement}`);
       } else {
         // 如果没有前一日数据，直接使用当日平均增量
         predictedIncrement = currentDayAvg;
         // console.log(`时段${hourStr}: 仅使用当日平均${currentDayAvg}, 预估${predictedIncrement}`);
       }
       
       // 确保预估值合理（不超过当日最大增量的2倍）
       const maxDailyIncrement = Math.max(...actualData.filter(item => item.sales_increment > 0).map(item => item.sales_increment));
       if (maxDailyIncrement > 0) {
         predictedIncrement = Math.min(predictedIncrement, maxDailyIncrement * 2);
       }
      
      predictedIncrement = Math.max(0, predictedIncrement);
      
      // 检查是否是连接点：第一个预估数据点需要连接到最后一个实际数据点
      const isTransitionPoint = (hour === firstPredictedHour && lastActualHour >= 0);
      
      fullDayData.push({
        hour: hourStr,
        sales_increment: predictedIncrement,
        actual_increment: null,
        predicted_increment: predictedIncrement,
        transition_increment: isTransitionPoint ? predictedIncrement : null,
        time_label: timeLabel,
        has_data: false,
        is_predicted: true,
        is_transition: isTransitionPoint
      });
    }
  }

  // 如果存在连接点，在最后一个实际数据点也添加连接数值
  if (lastActualHour >= 0 && firstPredictedHour >= 0) {
    const lastActualIndex = fullDayData.findIndex(item => item.hour === lastActualHour.toString().padStart(2, '0'));
    if (lastActualIndex >= 0) {
      fullDayData[lastActualIndex].transition_increment = lastActualValue;
    }
  }
  
  return fullDayData;
};

/**
 * 分时销量趋势图组件
 * 显示指定日期24小时内每个小时的销量增量趋势
 */
const HourlyTrendChart: React.FC<HourlyTrendChartProps> = ({
  data = [],
  height = 250,
  title = "分时销量走势",
  productData = null
}) => {
  
  // 日期选择状态
  const [selectedDate, setSelectedDate] = useState(getChinaDate());
  const [dateOption, setDateOption] = useState<'today' | 'yesterday' | 'custom'>('today');

  /**
   * 获取产品地区（智能识别产品所在地区）
   */
  const getProductRegion = (): string => {
    // 如果有产品数据，从SKU货币信息推断地区
    if (productData?.skus && Array.isArray(productData.skus) && productData.skus.length > 0) {
      const firstSku = productData.skus[0];
      if (firstSku?.currency) {
        const currency = firstSku.currency;
        const currencyToRegionMap: { [key: string]: string } = {
          'USD': 'US',    // 美元 -> 美国
          'GBP': 'GB',    // 英镑 -> 英国
          'EUR': 'DE',    // 欧元 -> 德国（欧洲代表）
          'SGD': 'SG',    // 新加坡元 -> 新加坡
          'MYR': 'MY',    // 马来西亚林吉特 -> 马来西亚
          'IDR': 'ID',    // 印尼盾 -> 印度尼西亚
          'PHP': 'PH',    // 菲律宾比索 -> 菲律宾
          'THB': 'TH',    // 泰铢 -> 泰国
          'VND': 'VN',    // 越南盾 -> 越南
          'MXN': 'MX',    // 墨西哥比索 -> 墨西哥
          'CAD': 'US',    // 加拿大元 -> 使用美国时区
          'AUD': 'US',    // 澳元 -> 使用美国时区
        };
        
        if (currencyToRegionMap[currency]) {
          return currencyToRegionMap[currency];
        }
      }
    }
    
    // 默认返回美国地区
    return 'US';
  };

  /**
   * 地区到时区的映射
   */
  const getTimezoneByRegion = (region: string): string => {
    const timezoneMap: { [key: string]: string } = {
      'US': 'America/New_York',    // 美国东部时间（EST/EDT）
      'GB': 'Europe/London',       // 英国时间（GMT/BST）
      'IE': 'Europe/Dublin',       // 爱尔兰时间（GMT/IST）
      'ES': 'Europe/Madrid',       // 西班牙时间（CET/CEST）
      'DE': 'Europe/Berlin',       // 德国时间（CET/CEST）
      'MX': 'America/Mexico_City', // 墨西哥时间（CST/CDT）
      'ID': 'Asia/Jakarta',        // 印度尼西亚时间（WIB）
      'SG': 'Asia/Singapore',      // 新加坡时间（SGT）
      'MY': 'Asia/Kuala_Lumpur',   // 马来西亚时间（MYT）
      'PH': 'Asia/Manila',         // 菲律宾时间（PHT）
      'TH': 'Asia/Bangkok',        // 泰国时间（ICT）
      'VN': 'Asia/Ho_Chi_Minh',    // 越南时间（ICT）
    };
    return timezoneMap[region] || 'America/New_York'; // 默认使用美国东部时间
  };

  /**
   * 获取时区缩写显示
   */
  const getTimezoneAbbr = (region: string): string => {
    const timezoneAbbrMap: { [key: string]: string } = {
      'US': 'EST',  // 美国东部时间
      'GB': 'GMT',  // 英国格林威治时间
      'IE': 'GMT',  // 爱尔兰时间
      'ES': 'CET',  // 中欧时间
      'DE': 'CET',  // 德国中欧时间
      'MX': 'CST',  // 墨西哥中部标准时间
      'ID': 'WIB',  // 西印度尼西亚时间
      'SG': 'SGT',  // 新加坡时间
      'MY': 'MYT',  // 马来西亚时间
      'PH': 'PHT',  // 菲律宾时间
      'TH': 'ICT',  // 泰国印度支那时间
      'VN': 'ICT',  // 越南印度支那时间
    };
    return timezoneAbbrMap[region] || 'EST';
  };

  /**
   * 将中国时间的小时转换为当地时间的小时
   * 使用与ProductDetailView中formatLocalTime相同的时区转换逻辑
   */
  const convertToLocalHour = (chinaHour: number, region: string): number => {
    try {
      // 获取目标时区
      const targetTimezone = getTimezoneByRegion(region);
      
      // 构造中国时间的ISO字符串
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const hour = String(chinaHour).padStart(2, '0');
      
      // 创建标准时间字符串，然后转换为Date对象
      const timeString = `${year}-${month}-${day}T${hour}:00:00+08:00`; // 明确指定中国时区UTC+8
      const date = new Date(timeString);
      
      // 使用toLocaleTimeString获取目标时区的小时
      const localTimeString = date.toLocaleTimeString('en-US', {
        timeZone: targetTimezone,
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      
      // 从"HH:mm:ss"格式中提取小时
      const localHour = parseInt(localTimeString.split(':')[0]);
      
      return localHour;
    } catch (error) {
      console.error('时区转换失败:', error);
      // 出错时使用简单的时差计算作为备用方案
      const timezoneOffsets: { [key: string]: number } = {
        'US': -13,   // 美国东部时间相对中国时间的差值
        'GB': -8,    // 英国时间相对中国时间的差值
        'SG': 0,     // 新加坡时间与中国时间相同
        'ID': -1,    // 印尼时间相对中国时间的差值
        'MY': 0,     // 马来西亚时间与中国时间相同
        'PH': 0,     // 菲律宾时间与中国时间相同
        'TH': -1,    // 泰国时间相对中国时间的差值
        'VN': -1,    // 越南时间相对中国时间的差值
        'MX': -14,   // 墨西哥时间相对中国时间的差值
        'ES': -7,    // 西班牙时间相对中国时间的差值
        'DE': -7,    // 德国时间相对中国时间的差值
        'IE': -8,    // 爱尔兰时间相对中国时间的差值
      };
      const offset = timezoneOffsets[region] || -13; // 默认使用美国时差
      const localHour = (chinaHour + offset + 24) % 24; // 确保结果在0-23范围内
      return localHour;
    }
  };
  
  // 获取有数据的日期列表
  const availableDates = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const dates = new Set<string>();
    data.forEach(record => {
      if (record && record.created_at && typeof record.created_at === 'string') {
        try {
          const recordDate = record.created_at.split(' ')[0];
          dates.add(recordDate);
        } catch (error) {
          console.warn('解析记录日期时出错:', error, record);
        }
      }
    });
    
    return Array.from(dates).sort().reverse(); // 最新日期在前
  }, [data]);
  
  // 当日期选项改变时，更新选中的日期
  useEffect(() => {
    const today = getChinaDate();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = formatToChinaTime(yesterday.toISOString()).split(' ')[0];
    
    switch (dateOption) {
      case 'today':
        setSelectedDate(today);
        break;
      case 'yesterday':
        setSelectedDate(yesterdayStr);
        break;
      case 'custom':
        // 保持当前选中的日期不变
        break;
    }
  }, [dateOption]);
  
  /**
   * 处理选中日期的数据，按小时分组并计算正确的增量
   */
  const hourlyData = useMemo(() => {
    // 筛选选中日期的数据并按时间排序
    const selectedDateRecords = (data || [])
      .filter(record => {
        // 防御性检查：确保record和created_at存在
        if (!record || !record.created_at || typeof record.created_at !== 'string') {
          return false;
        }
        
        try {
          // 解析记录的日期部分 (YYYY-MM-DD HH:mm:ss 格式)
          const recordDate = record.created_at.split(' ')[0];
          return recordDate === selectedDate;
        } catch (error) {
          console.warn('解析记录时间时出错:', error, record);
          return false;
        }
      })
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    if (selectedDateRecords.length === 0) {
      return generate24HoursData([], selectedDate, data);
    }

    // 按小时分组并获取每小时的代表性数据点
    const hourlyGroups = new Map<string, any[]>();
    
    selectedDateRecords.forEach(record => {
      try {
        const timePart = record.created_at.split(' ')[1];
        if (!timePart) {
          console.warn('时间部分格式不正确:', record.created_at);
          return;
        }
        
        const hour = timePart.split(':')[0]; // 提取小时 (00-23)
        if (!hour || isNaN(parseInt(hour))) {
          console.warn('小时格式不正确:', timePart);
          return;
        }
        
        if (!hourlyGroups.has(hour)) {
          hourlyGroups.set(hour, []);
        }
        hourlyGroups.get(hour)!.push(record);
      } catch (error) {
        console.warn('处理记录时出错:', error, record);
      }
    });

    // 使用完全按照"历史记录详情"的逐条增量计算方式
    // 先对所有选中日期记录按时间排序，然后计算每条记录的增量，最后按小时分组汇总
    
    // 1. 按时间排序所有选中日期记录
    const sortedSelectedDateRecords = [...selectedDateRecords].sort((a, b) => 
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    
    // 2. 计算每条记录相对于前一条的增量（和历史记录详情完全一致）
    const recordsWithIncrements = sortedSelectedDateRecords.map((record, index) => {
      let increment = 0;
      if (index > 0) {
        const previous = sortedSelectedDateRecords[index - 1];
        increment = Math.max(0, record.sales_count - previous.sales_count);
      } else {
        // 对于一天的第一条记录，我们需要特殊处理
        // 检查是否有前一天的数据作为基准
        const previousDate = new Date(selectedDate + ' 00:00:00');
        previousDate.setDate(previousDate.getDate() - 1);
        const previousDateStr = formatToChinaTime(previousDate.toISOString()).split(' ')[0];
        
        const previousDayRecords = data.filter(r => {
          const recordDate = r.created_at.split(' ')[0];
          return recordDate === previousDateStr;
        });
        
        if (previousDayRecords.length > 0) {
          // 使用前一天最后一条记录作为基准
          const lastPreviousDayRecord = previousDayRecords.sort((a, b) => 
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )[0];
          increment = Math.max(0, record.sales_count - lastPreviousDayRecord.sales_count);
        }
      }
      
      return {
        ...record,
        sales_increment: increment,
        hour: record.created_at.split(' ')[1].split(':')[0]
      };
    });
    
    // 3. 按小时分组并汇总增量
    const hourlyRecords: any[] = [];
    
    for (let hour = 0; hour < 24; hour++) {
      const hourStr = hour.toString().padStart(2, '0');
      const hourRecords = recordsWithIncrements.filter(r => r.hour === hourStr);
      
      if (hourRecords.length > 0) {
        // 汇总该小时内所有记录的增量
        const totalHourIncrement = hourRecords.reduce((sum, record) => 
          sum + record.sales_increment, 0
        );
        
        // 使用该时段最新的记录作为代表
        const lastRecord = hourRecords[hourRecords.length - 1];
        
        hourlyRecords.push({
          hour: hourStr,
          sales_count: lastRecord.sales_count,
          sales_increment: totalHourIncrement,
          created_at: lastRecord.created_at,
          record_count: hourRecords.length,
          detail_records: hourRecords // 用于调试
        });
      }
    }

    // 按小时顺序排序
    const hourlyDataWithIncrements = hourlyRecords.sort((a, b) => 
      parseInt(a.hour) - parseInt(b.hour)
    );

    // 计算各时段增量总和（用于验证）
    // const totalDailyIncrement = hourlyDataWithIncrements.reduce((sum, item) => 
    //   sum + item.sales_increment, 0
    // );
    






    return generate24HoursData(hourlyDataWithIncrements, selectedDate, data);
  }, [data, selectedDate]);

  /**
   * 计算统计信息
   */
  const statistics = useMemo(() => {
    if (!hourlyData || hourlyData.length === 0 || !data || data.length === 0) {
      return {
        totalIncrement: 0,
        maxIncrement: 0,
        avgIncrement: 0,
        predictedCount: 0,
        actualCount: 0,
        forecastMethod: 'simple_average'
      };
    }
    
    // 使用与图表一致的增量计算方式：各时段增量累加
    const actualDataItems = hourlyData.filter(item => item.has_data && item.actual_increment !== null && item.actual_increment > 0);
    const realTotalIncrement = actualDataItems.reduce((sum, item) => sum + (item.actual_increment || 0), 0);
    
    // 时段统计仍然保持原有逻辑（用于显示各时段的峰值和平均值）
    const actualData = hourlyData.filter(item => item.has_data);
    const predictedData = hourlyData.filter(item => item.is_predicted && item.predicted_increment && item.predicted_increment > 0);
    
    const increments = actualData.map(item => item.sales_increment || 0);
    const maxIncrement = increments.length > 0 ? Math.max(...increments) : 0;
    const avgIncrement = actualData.length > 0 ? 
      Math.round(increments.reduce((sum, inc) => sum + inc, 0) / actualData.length) : 0;
    
    return {
      totalIncrement: realTotalIncrement, // 使用真实的今日增量
      maxIncrement,
      avgIncrement,
      predictedCount: predictedData.length, // 预估时段数量
      actualCount: actualData.length, // 实际数据时段数量
      forecastMethod: 'simple_average' // 固定使用简单平均方法
    };
  }, [hourlyData, data]);

  /**
   * 自定义实际增量标签
   */
  const ActualIncrementLabel = (props: any) => {
    const { x, y, value, payload, ...restProps } = props;
    // 不显示0值标签或占位空间标签
    if (!value || value <= 0 || payload?.hour === 'spacer') return null; 
    
    // 过滤掉React不识别的DOM属性
    const { 
      dataKey, 
      index, 
      points, 
      parentViewBox, 
      textBreakAll, 
      key,
      ...cleanProps 
    } = restProps;
    
    return (
      <text 
        x={x} 
        y={Math.max(y - 20, 15)} // 确保标签不会超出图表顶部
        textAnchor="middle" 
        fontSize="14" 
        fill="#3b82f6" 
        fontWeight="bold"
        {...cleanProps}
      >
        +{value}
      </text>
    );
  };

  /**
   * 自定义预估增量标签
   */
  const PredictedIncrementLabel = (props: any) => {
    const { x, y, value, payload, ...restProps } = props;
    // 不显示0值标签或占位空间标签
    if (!value || value <= 0 || payload?.hour === 'spacer') return null; 
    
    // 过滤掉React不识别的DOM属性
    const { 
      dataKey, 
      index, 
      points, 
      parentViewBox, 
      textBreakAll, 
      key,
      ...cleanProps 
    } = restProps;
    
    return (
      <text 
        x={x} 
        y={Math.max(y - 20, 15)} // 确保标签不会超出图表顶部
        textAnchor="middle" 
        fontSize="14" 
        fill="#f59e0b" 
        fontWeight="bold"
        {...cleanProps}
      >
        +{value}
      </text>
    );
  };

  /**
   * 自定义工具提示
   */
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      // 占位空间不显示工具提示
      if (!data || data.hour === 'spacer') return null;
      
      // 解析小时数，处理"X时"格式
      const hourNum = label.replace('时', '');
      const hourStr = hourNum.padStart(2, '0');
      const chinaHour = parseInt(hourNum);
      
      // 获取产品地区和当地时间
      const productRegion = getProductRegion();
      const localHour = convertToLocalHour(chinaHour, productRegion);
      const timezoneAbbr = getTimezoneAbbr(productRegion);
      
      return (
        <div className="bg-base-100 border border-base-300 rounded-lg shadow-lg p-3 min-w-[200px]">
          <p className="text-sm font-semibold text-base-content mb-2">
            ⏰ 时间段：{hourStr}:00:00 - {hourStr}:59:59
          </p>
          
          {/* 当地时段信息 */}
          <p className="text-sm text-blue-600 mb-3">
            🌍 当地时段：{localHour}时 ({productRegion}/{timezoneAbbr})
          </p>
          
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-base-content/70">时段增量：</span>
              <span className={`text-sm font-medium ${
                data.sales_increment > 0 ? 'text-success' : 'text-base-content/50'
              }`}>
                +{data.sales_increment.toLocaleString('zh-CN')}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-base-content/70">数据状态：</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                data.is_predicted 
                  ? 'bg-warning/20 text-warning' 
                  : data.has_data 
                    ? 'bg-success/20 text-success' 
                    : 'bg-base-200 text-base-content/50'
              }`}>
                {data.is_predicted ? '预估数据' : data.has_data ? '实际数据' : '无数据'}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };



  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        {/* 标题和日期选择 */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <h3 className="card-title">
              <span className="text-accent">⏰</span>
              {title}
            </h3>
            
            {/* 日期选择器 - 移到标题右边 */}
            {/* 日期选择器 - 移到标题右边 */}
            <div className="flex flex-wrap items-center gap-3">
              {/* 快速选项 */}
              <div className="flex gap-2">
                <button
                  className={`btn btn-sm ${dateOption === 'today' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setDateOption('today')}
                >
                  今日
                </button>
                <button
                  className={`btn btn-sm ${dateOption === 'yesterday' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setDateOption('yesterday')}
                  disabled={availableDates.length === 0}
                >
                  昨日
                </button>
              </div>
              
              {/* 自定义日期选择 */}
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="dateOption"
                  className="radio radio-primary radio-sm"
                  checked={dateOption === 'custom'}
                  onChange={() => setDateOption('custom')}
                />
                <select
                  className="select select-sm select-bordered"
                  value={dateOption === 'custom' ? selectedDate : ''}
                  onChange={(e) => {
                    setDateOption('custom');
                    setSelectedDate(e.target.value);
                  }}
                  disabled={availableDates.length === 0}
                >
                  <option value="" disabled>选择日期</option>
                  {availableDates.map(date => (
                    <option key={date} value={date}>
                      {date} {date === getChinaDate() ? '(今日)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {availableDates.length === 0 && (
                <div className="text-xs text-warning">暂无历史数据</div>
              )}
            </div>
          </div>
          
          {/* 统计信息面板 */}
          <div className="flex gap-3">
            <div className="bg-info/10 rounded-lg px-3 py-2 border border-info/20 text-center">
              <div className="text-xs text-info/70">单时段峰值</div>
              <div className="text-sm font-semibold text-info">
                +{statistics.maxIncrement.toLocaleString('zh-CN')}
              </div>
            </div>
            
            <div className="bg-warning/10 rounded-lg px-3 py-2 border border-warning/20 text-center">
              <div className="text-xs text-warning/70">平均增量</div>
              <div className="text-sm font-semibold text-warning">
                +{statistics.avgIncrement.toLocaleString('zh-CN')}
              </div>
            </div>
            
            <div className="bg-success/10 rounded-lg px-3 py-2 border border-success/20 text-center">
              <div className="text-xs text-success/70">{selectedDate === getChinaDate() ? '今日' : '当日'}增量</div>
              <div className="text-sm font-semibold text-success">
                +{statistics.totalIncrement.toLocaleString('zh-CN')}
              </div>
            </div>
            
            <div className="bg-accent/10 rounded-lg px-3 py-2 border border-accent/20 text-center">
              <div className="text-xs text-accent/70">查看日期</div>
              <div className="text-sm font-semibold text-accent">
                {selectedDate === getChinaDate() ? '今日' : selectedDate.split('-').slice(1).join('/')}
              </div>
            </div>
            
            {/* 预估统计信息 - 仅今日显示 */}
            {selectedDate === getChinaDate() && statistics.predictedCount > 0 && (
              <div className="bg-orange-100 rounded-lg px-3 py-2 border border-orange-200 text-center">
                <div className="text-xs text-orange-600">预估时段</div>
                <div className="text-sm font-semibold text-orange-700">
                  {statistics.predictedCount}个
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 图表区域 */}
        <div className="w-full">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={hourlyData} margin={{ top: 40, right: 50, left: 10, bottom: 5 }}>
            <defs>
              <linearGradient id="incrementGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e5e7eb" 
              opacity={0.5}
            />
            
            <XAxis 
              dataKey="time_label"
              axisLine={{ stroke: '#000000', strokeWidth: 1 }}
              tickLine={{ stroke: '#000000', strokeWidth: 1 }}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              interval={0} // 显示所有时间刻度
            />
            
            <YAxis 
              axisLine={{ stroke: '#000000', strokeWidth: 1 }}
              tickLine={{ stroke: '#000000', strokeWidth: 1 }}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              tickFormatter={(value) => value.toLocaleString('zh-CN')}
              domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.3)]}
              width={50}
            />
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* 实际数据线 - 蓝色，使用平滑曲线 */}
            <Line
              key="actual-line"
              type="monotone"
              dataKey="actual_increment"
              stroke="#3b82f6"
              strokeWidth={3}
              connectNulls={false}
              activeDot={false}
              dot={(props) => {
                const { payload, cx, cy, index } = props;
                // 占位空间或无有效数据不显示数据点
                if (!payload || payload.hour === 'spacer' || !payload.actual_increment || payload.actual_increment <= 0) return null;
                
                return (
                  <circle 
                    key={`actual-dot-${payload.hour}`}
                    cx={cx} 
                    cy={cy} 
                    fill="#3b82f6" 
                    stroke="#fff" 
                    strokeWidth={2} 
                    r={5}
                  />
                );
              }}
              strokeDasharray="0"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {/* 添加数据标签 */}
              <LabelList key="actual-labels" dataKey="actual_increment" content={<ActualIncrementLabel />} />
            </Line>
            
            {/* 连接线 - 从实际数据到预估数据的橙色虚线连接 */}
            <Line
              key="transition-line"
              type="monotone"
              dataKey="transition_increment"
              stroke="#f59e0b"
              strokeWidth={3}
              strokeDasharray="8 4"
              connectNulls={false}
              activeDot={false}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
            />

            {/* 预估数据线 - 使用平滑曲线 */}
            <Line
              key="predicted-line"
              type="monotone"
              dataKey="predicted_increment"
              stroke="#f59e0b"
              strokeWidth={3}
              strokeDasharray="8 4"
              connectNulls={true}
              activeDot={false}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={(props) => {
                const { payload, cx, cy, index } = props;
                // 占位空间或无有效预估数据不显示数据点
                if (!payload || payload.hour === 'spacer' || !payload.is_predicted || !payload.predicted_increment || payload.predicted_increment <= 0) return null;
                
                return (
                  <circle 
                    key={`predicted-dot-${payload.hour}`}
                    cx={cx} 
                    cy={cy} 
                    fill="#f59e0b" 
                    stroke="#fff" 
                    strokeWidth={1} 
                    r={3}
                  />
                );
              }}
            >
              {/* 为预估数据也添加数据标签 */}
              <LabelList key="predicted-labels" dataKey="predicted_increment" content={<PredictedIncrementLabel />} />
            </Line>
            
            {/* 时段均销量水平虚线 - 与平均增量信息卡片数据一致 */}
            {statistics.avgIncrement > 0 && (
              <ReferenceLine
                y={statistics.avgIncrement}
                stroke="#9CA3AF"
                strokeDasharray="6 4"
                strokeWidth={1.5}
                strokeOpacity={0.7}
                label={{
                  value: `时段均销量: ${statistics.avgIncrement.toLocaleString('zh-CN')}`,
                  position: "insideTopLeft",
                  style: { 
                    fontSize: "11px", 
                    fill: "#6B7280", 
                    fontWeight: "500",
                    textShadow: "1px 1px 2px rgba(255,255,255,0.8)"
                  }
                }}
              />
            )}

            {/* 当前小时参考线 - 仅今日显示 */}
            {selectedDate === getChinaDate() && (
            <ReferenceLine 
                x={`${parseInt(formatToChinaTime(new Date().toISOString()).split(' ')[1].split(':')[0])}时`}
              stroke="#ef4444" 
              strokeDasharray="2 2" 
              strokeWidth={1}
              label={{ value: "当前时间", position: "insideTopRight", fontSize: 10, fill: "#ef4444" }}
            />
            )}
          </LineChart>
        </ResponsiveContainer>
        
        {/* 图表说明 */}
        <div className="flex items-center justify-center gap-6 mt-3 text-xs text-base-content/60">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>实际增量</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-warning" style={{borderStyle: 'dashed'}}></div>
            <span>预估增量</span>
          </div>
            {selectedDate === getChinaDate() && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-0.5 bg-red-500"></div>
            <span>当前时间</span>
          </div>
            )}
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            <span>无数据</span>
          </div>
            {/* 预估算法说明 */}
            {selectedDate === getChinaDate() && statistics.predictedCount > 0 && (
              <div className="flex items-center gap-2 border-l border-gray-300 pl-3">
                <span className="text-orange-600">预估基于：当日平均增量 + 前日同时段参考</span>
              </div>
            )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default HourlyTrendChart; 